import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Collapse from "@mui/material/Collapse"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"
import SelectSMSCampaignAutoComplete, {
  useSelectSMSCampaignAutoCompleteState,
} from "./SelectSMSCampaignAutoComplete"
import UpdateCreateSMSCampaignDialog, {
  useCreateSMSCampaignDialogState,
} from "./UpdateCreateSMSCampaignDialog"

export default function SelectSMSCampaignDialog({
  open,
  onClose,
  onCampaignCreated = () => {},
  campaignTextPropName,
  primaryButton = { text: "", onClick: () => {}, isLoading: false },
  parentSelectedCampaign,
  campaignsAPIURL,
  errorMessage,
}) {
  const {
    selectedCampaign,
    setSelectedCampaign,
    campaignInputValue,
    setCampaignInputValue,
    availableCampaigns,
    setAvailableCampaigns,
    availableCampaignsOptions,
    campaignsLoaded,
  } = useSelectSMSCampaignAutoCompleteState(open, campaignsAPIURL)
  const {
    createSMSCampaignDialogOpen,
    setCreateSMSCampaignDialogOpen,
    handleCreateCampaignDialogClosed,
  } = useCreateSMSCampaignDialogState()
  const Container = useDialogContainer()

  function handlePrimaryButtonClicked() {
    primaryButton.onClick(selectedCampaign)
  }

  function handleDialogClosed(e) {
    onClose()
  }

  function handleCreateLinkClicked() {
    onClose()
    setCreateSMSCampaignDialogOpen(true)
  }

  useEffect(
    () => {
      if (!parentSelectedCampaign) {
        return
      }
      setSelectedCampaign(
        availableCampaignsOptions.filter(
          (campaign) => campaign.id === parentSelectedCampaign.id
        )[0]
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentSelectedCampaign]
  )

  return (
    <Container
      open={open}
      onClose={handleDialogClosed}
      anchor="right"
      maxWidth="xs"
      fullWidth
      keepMounted
    >
      <DialogHeader title="Select SMS Template" onClose={onClose} />
      <DialogContent>
        <Column>
          <SelectSMSCampaignAutoComplete
            availableCampaigns={availableCampaigns}
            availableCampaignsOptions={availableCampaignsOptions}
            campaignInputValue={campaignInputValue}
            setCampaignInputValue={setCampaignInputValue}
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            campaignsLoaded={campaignsLoaded}
            onCreateLinkClicked={handleCreateLinkClicked}
          />
          <Collapse in={!!selectedCampaign}>
            <Column sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={500}>
                SMS Preview
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                whiteSpace="pre-wrap"
                sx={{ mt: 1 }}
              >
                {selectedCampaign
                  ? selectedCampaign[campaignTextPropName]
                  : null}
              </Typography>
            </Column>
          </Collapse>
          {errorMessage ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleDialogClosed}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={!selectedCampaign}
          loading={primaryButton.isLoading}
          onClick={handlePrimaryButtonClicked}
        >
          {primaryButton.text}
        </LoadingButton>
      </DialogActions>
      <UpdateCreateSMSCampaignDialog
        open={createSMSCampaignDialogOpen}
        onClose={handleCreateCampaignDialogClosed(
          setAvailableCampaigns,
          onCampaignCreated
        )}
      />
    </Container>
  )
}

export function useBaseSelectSMSCampaignState(open, apiURL) {
  const [availableCampaigns, setAvailableCampaigns] = useState([])
  const [campaignsLoaded, setCampaignsLoaded] = useState(false)
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!open || campaignsLoaded) {
        return
      }

      axios
        .get(apiURL)
        .then(({ data }) => {
          setAvailableCampaigns(data)
        })
        .finally(() => setCampaignsLoaded(true))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, apiURL]
  )

  return {
    availableCampaigns,
    setAvailableCampaigns,
    campaignsLoaded,
  }
}
